import React, { useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";
import L from "leaflet";

// react-leaflet-cluster exposes MarkerCluster via its augmented leaflet module;
// @types/leaflet does not export it directly, so we declare a minimal shape here.
interface MarkerCluster {
  getAllChildMarkers(): L.Marker[];
  getChildCount(): number;
}

import mapMarker from "@/assets/images/map_marker.svg";
import mapMarkerInactive from "@/assets/images/map_marker_inactive.svg";
import mapMarkerSelected from "@/assets/images/map_marker_selected.svg";
import { hasValidCoordinates, SiteInfo } from "./types";

const myIcon = new L.Icon({
  iconUrl: mapMarker,
  iconRetinaUrl: mapMarker,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const myIconSelected = new L.Icon({
  iconUrl: mapMarkerSelected,
  iconRetinaUrl: mapMarkerSelected,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const myIconInactive = new L.Icon({
  iconUrl: mapMarkerInactive,
  iconRetinaUrl: mapMarkerInactive,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const createClusterCustomIcon = function (
  cluster: MarkerCluster,
  subsetSitesData: SiteInfo[],
) {
  const sitesInCluster = cluster.getAllChildMarkers();
  const sitesInClusterLatLong = sitesInCluster.map((site: L.Marker) => [
    site.getLatLng().lat,
    site.getLatLng().lng,
  ]);

  const sitesInClusterWithData = sitesInClusterLatLong.some((site: number[]) =>
    subsetSitesData.some(
      (s: SiteInfo) =>
        hasValidCoordinates(s) &&
        s.coordinates.lat === site[0] &&
        s.coordinates.lng === site[1],
    ),
  );

  return L.divIcon({
    html: `<span
    class="${
      sitesInClusterWithData ? "bg-[#0C98D0]" : "bg-[#CCC]"
    } w-[40px] h-[40px] rounded-full flex items-center justify-center border border-white font-bold text-white text-[14px]"
    >${cluster.getChildCount()}</span>`,
    className: "customMarker",
    iconSize: L.point(40, 40, true),
  });
};

interface MemoizedMarkerProps {
  site: SiteInfo;
  isSelected: boolean;
  isActive: boolean;
  onSelect: (site: SiteInfo) => void;
}

const MemoizedMarker = React.memo(
  function MemoizedMarker({
    site,
    isSelected,
    isActive,
    onSelect,
  }: MemoizedMarkerProps) {
    const icon = isSelected
      ? myIconSelected
      : isActive
        ? myIcon
        : myIconInactive;

    return (
      <Marker
        key={`${site.site}-${site.coordinates?.lat}-${site.coordinates?.lng}`}
        icon={icon}
        eventHandlers={{
          click: () => onSelect(site),
        }}
        data-site={site}
        position={[site.coordinates.lat, site.coordinates.lng]}
      />
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isActive === nextProps.isActive &&
      prevProps.site.site === nextProps.site.site
    );
  },
);

const DEFAULT_CENTER: L.LatLngExpression = [10, -0.09];
const DEFAULT_ZOOM = 1.8;

function ResetZoomButton() {
  const map = useMap();
  return (
    <button
      data-testid="sites-overview-reset-zoom-button"
      className="absolute top-3 right-3 z-1000 rounded-sm bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100"
      onClick={() => map.setView(DEFAULT_CENTER, DEFAULT_ZOOM)}
    >
      Reset Zoom
    </button>
  );
}

const MapChart = ({
  mapHeight,
  fullSitesData,
  subsetSitesData,
  setSelectedSite,
  selectedSite,
}: {
  mapHeight: number;
  fullSitesData: SiteInfo[];
  subsetSitesData: SiteInfo[];
  setSelectedSite: (site: SiteInfo) => void;
  selectedSite?: SiteInfo;
}) => {
  const clusterFunction = useCallback(
    (cluster: MarkerCluster) => {
      return createClusterCustomIcon(cluster, subsetSitesData);
    },
    [subsetSitesData],
  );

  const markers = useMemo(() => {
    return fullSitesData
      .filter(hasValidCoordinates)
      .map((site) => (
        <MemoizedMarker
          key={`${site.site}-${site.coordinates.lat}-${site.coordinates.lng}`}
          site={site}
          isSelected={selectedSite?.site === site.site}
          isActive={subsetSitesData.some(
            (s) =>
              hasValidCoordinates(s) &&
              s.coordinates.lat === site.coordinates.lat &&
              s.coordinates.lng === site.coordinates.lng,
          )}
          onSelect={setSelectedSite}
        />
      ));
  }, [fullSitesData, subsetSitesData, selectedSite, setSelectedSite]);

  return (
    <div data-testid="sites-overview-world-map" className="w-full relative z-0">
      <MapContainer
        center={DEFAULT_CENTER}
        className="w-full bg-[#E3F1F4] relative z-0"
        style={{ height: `${mapHeight}px` }}
        zoom={DEFAULT_ZOOM}
        zoomSnap={0}
        scrollWheelZoom={true}
        wheelDebounceTime={100}
        wheelPxPerZoomLevel={120}
        attributionControl={false}
        maxZoom={15}
        minZoom={0}
        zoomControl
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
      >
        <ResetZoomButton />
        <TileLayer
          noWrap={true}
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          crossOrigin="anonymous"
        />

        <MarkerClusterGroup
          iconCreateFunction={clusterFunction}
          showCoverageOnHover={false}
          key={subsetSitesData.length}
          removeOutsideVisibleBounds={true}
          animate={false}
          chunkedLoading={true}
          spiderfyOnMaxZoom={false}
        >
          {markers}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default React.memo(MapChart);
